<template>
  <div class="algoritma-management">
    <div class="page-header">
      <div>
        <h1>Alur Algoritma Klinis</h1>
      </div>
    </div>

    <div class="content-container">
      <div class="filters-section">
        <a-row :gutter="16">
          <a-col :xs="24" :sm="16" :md="20">
            <a-input
              v-model:value="searchQuery"
              placeholder="Cari nama algoritma..."
              size="large"
              allow-clear
            >
              <template #prefix>
                <SearchOutlined style="color: rgba(0, 0, 0, 0.25); margin-right: 4px" />
              </template>
            </a-input>
          </a-col>
          <a-col :xs="24" :sm="8" :md="4">
            <button class="tambah-button" @click="goToCreate">
              <plus-outlined />
              TAMBAH
            </button>
          </a-col>
        </a-row>
      </div>

      <div class="content-container">
        <div class="table-card">
          <a-table
            :columns="columns"
            :data-source="algoritmaList"
            :loading="loading"
            :pagination="pagination"
            @change="handleTableChange"
            row-key="id"
            class="custom-table"
            :custom-row="customRow"
          >
            <template #no="{ index }">
              {{ (pagination.current - 1) * pagination.pageSize + index + 1 }}
            </template>

            <template #loading>
              <div class="custom-loading">
                <a-spin size="large" />
                <p class="loading-text">Memuat data...</p>
              </div>
            </template>

            <template #status="{ record }">
              <div class="status-cell" @click.stop>
                <a-switch
                  :checked="record.status === 'active'"
                  @change="checked => handleStatusChange(record, checked)"
                  class="custom-switch"
                  :class="{ 'switch-active': record.status === 'active' }"
                />
                <a-tag
                  :class="[
                    'status-badge',
                    record.status === 'active' ? 'badge-active' : 'badge-inactive',
                  ]"
                >
                  {{ record.status === 'active' ? 'Aktif' : 'Nonaktif' }}
                </a-tag>
              </div>
            </template>

            <template #action="{ record }">
              <div class="action-buttons">
                <button class="btn-action-pill edit-btn" @click.stop="goToEdit(record)">
                  <edit-outlined /> UBAH
                </button>

                <a-popconfirm
                  title="Yakin ingin menghapus algoritma ini? Semua node juga akan dihapus."
                  ok-text="Ya"
                  cancel-text="Tidak"
                  @confirm="deleteAlgoritma(record)"
                  placement="topRight"
                >
                  <button class="btn-action-pill delete-btn" @click.stop>
                    <delete-outlined /> HAPUS
                  </button>
                </a-popconfirm>
              </div>
            </template>
          </a-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { message } from 'ant-design-vue'
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons-vue'
import { useRouter } from 'vue-router'
import { mobileCmsAlgoritmaAPI } from '@/services/axios/perki/mobileCms'
import { debounce } from 'lodash-es'

export default {
  name: 'MobileCmsAlgoritmaKlinis',
  components: { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const algoritmaList = ref([])
    const searchQuery = ref('')

    const pagination = ref({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showTotal: total =>
        `Menampilkan ${(pagination.value.current - 1) * pagination.value.pageSize +
          1} sampai ${Math.min(
          pagination.value.current * pagination.value.pageSize,
          total,
        )} dari ${total} baris`,
    })

    const columns = [
      {
        title: 'No.',
        key: 'no',
        slots: { customRender: 'no' },
        width: 80,
      },
      {
        title: 'Nama Algoritma',
        dataIndex: 'nama',
        key: 'nama',
        sorter: true,
        sorter: (a, b) => a.nama.localeCompare(b.nama),
      },
      {
        title: 'Status',
        key: 'status',
        slots: { customRender: 'status' },
        width: 180,
      },
      {
        title: 'Aksi',
        key: 'action',
        slots: { customRender: 'action' },
        width: 250,
      },
    ]

    const customRow = record => {
      return {
        onClick: () => {
          goToEdit(record)
        },
        style: { cursor: 'pointer' },
      }
    }

    const loadAlgoritma = async (resetPage = false) => {
      loading.value = true
      if (resetPage) {
        pagination.value.current = 1
      }
      try {
        const params = {
          page: pagination.value.current,
          pageSize: pagination.value.pageSize,
        }
        if (searchQuery.value) params.q = searchQuery.value

        const response = await mobileCmsAlgoritmaAPI.getAll(params)
        const data = response.data.data
        algoritmaList.value = Array.isArray(data) ? data : []
        pagination.value.total = algoritmaList.value.length
      } catch (error) {
        message.error('Gagal memuat data algoritma')
        console.error(error)
      } finally {
        loading.value = false
      }
    }

    const debouncedSearch = debounce(() => {
      loadAlgoritma(true)
    }, 500)

    watch(searchQuery, () => {
      debouncedSearch()
    })

    const handleTableChange = pag => {
      pagination.value.current = pag.current
      pagination.value.pageSize = pag.pageSize
      loadAlgoritma()
    }

    const handleStatusChange = async (record, checked) => {
      const newStatus = checked ? 'active' : 'inactive'
      const oldStatus = record.status
      record.status = newStatus // Optimistic UI update

      try {
        await mobileCmsAlgoritmaAPI.updateStatus(record.id, { status: newStatus })
        message.success('Status berhasil diubah')
      } catch (error) {
        record.status = oldStatus // Revert on fail
        const msg =
          error.response?.data?.message || error.response?.data?.result || 'Gagal mengubah status'
        message.error(msg)
      }
    }

    const deleteAlgoritma = async record => {
      try {
        await mobileCmsAlgoritmaAPI.delete(record.id)
        message.success('Algoritma berhasil dihapus')
        loadAlgoritma()
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.result ||
          'Gagal menghapus algoritma'
        message.error(msg)
      }
    }

    const goToCreate = () => {
      router.push('/mobile-cms/algoritma-klinis/create')
    }

    const goToEdit = record => {
      router.push(`/mobile-cms/algoritma-klinis/edit/${record.id}`)
    }

    onMounted(() => {
      loadAlgoritma()
    })

    return {
      loading,
      algoritmaList,
      searchQuery,
      pagination,
      columns,
      customRow,
      loadAlgoritma,
      handleTableChange,
      handleStatusChange,
      deleteAlgoritma,
      goToCreate,
      goToEdit,
    }
  },
}
</script>

<style lang="scss">
.ant-table-column-sorter-up.on,
.ant-table-column-sorter-down.on {
  color: #e10f28 !important;
}
</style>

<style lang="scss" scoped>
@import '../styleViews.scss';

.algoritma-management {
  min-height: calc(100vh - 64px);
  background: #fcfcfc;

  @media (max-width: 768px) {
    padding: 4px;
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    border-radius: 8px;

    @media (max-width: 768px) {
      padding: 0;
    }

    h1 {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
      color: #282828;
    }
  }

  .content-container {
    border-radius: 8px;

    @media (max-width: 768px) {
      padding: 0;
    }

    .filters-section {
      margin-bottom: 24px;

      :deep(.ant-input::placeholder) {
        color: #999999 !important;
        opacity: 1 !important;
        font-size: 14px;
      }
    }
  }
}

.tambah-button {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28;
  width: 100%;
  background: #e10f28;
  color: white;
  padding: 6px 12px;
  transition: all 0.3s;
  font-family: 'Barlow', sans-serif !important;

  @media (max-width: 576px) {
    margin-top: 12px;
  }

  &:hover {
    background: white !important;
    border-color: #e10f28 !important;
    color: #e10f28 !important;
  }
}

.table-card {
  overflow: hidden;
}

/* Table Aesthetics */
::v-deep(.ant-table-thead > tr > th) {
  background: #f2f4f8 !important;
  font-weight: 600;
  color: #262626;
  padding: 16px 24px;
  border-right: none !important;
  border-bottom: 1px solid #e5e7eb;
}

::v-deep(.ant-table) {
  border-collapse: collapse;
  font-size: 14px;
}

::v-deep(.ant-table-tbody > tr) {
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }
}

::v-deep(.ant-table-bordered .ant-table-body > table) {
  border: none !important;
}

::v-deep(.ant-table-tbody > tr > td) {
  padding: 16px 24px;
  border-bottom: 1px solid #f3f4f6;
  border-right: none !important;
  background: white !important;
}

::v-deep(.ant-table-tbody
    > tr:hover:not(.ant-table-expanded-row):not(.ant-table-row-selected)
    > td) {
  background: #e4e9f0 !important;
}

::v-deep(.ant-table-tbody > tr > td.ant-table-column-sort) {
  background: white !important;
}

/* Status cell specifically */
.status-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-badge {
  font-size: 12px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 20px;
  border: 0.5px solid;
}

.badge-active {
  background-color: #fafafa;
  color: #183e6f;
  border-color: #d9d9d9;
}

.badge-inactive {
  background-color: #fafafa;
  color: #6b7280;
  border-color: #d9d9d9;
}

/* Custom Switch Styling matching Mockup */
:deep(.custom-switch.ant-switch) {
  background-color: #d1d5db;
}

:deep(.custom-switch.switch-active.ant-switch) {
  background-color: #dc2626;
}

.action-buttons {
  display: flex;
  justify-content: flex-start;
  gap: 8px;
}

.btn-action-pill {
  height: 32px;
  padding: 0 16px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  background: white;
  transition: all 0.2s;
}

.edit-btn {
  border: 2px solid var(--E-Office-Secondary, #282828);
  color: #282828;

  &:hover {
    border-color: #9ca3af;
    background: #f9fafb;
  }
}

.delete-btn {
  background: #1f2937;
  border: 1px solid #1f2937;
  color: white;

  &:hover {
    background: #374151;
    border-color: #374151;
  }
}

/* Pagination Styling */
:deep(.ant-pagination) {
  padding: 16px 24px;
  margin: 0 !important;
  border-top: 1px solid #f3f4f6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

:deep(.ant-pagination-total-text) {
  color: #6b7280;
  flex: 1;
  text-align: left;
}

.custom-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 16px;
}

.loading-text {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
}
</style>
